import { useState, useEffect } from "react";
import { TrendingUp, TrendingDown, BarChart2, Calendar, Loader2, DollarSign } from "lucide-react";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { formatPYGCompact } from "../utils/format";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, BarChart, Bar
} from "recharts";

const MONTHS_ES = ["Ene", "Feb", "Mar", "Abr", "May", "Jun", "Jul", "Ago", "Sep", "Oct", "Nov", "Dic"];

function StatCard({ title, value, sub, trend, icon: Icon }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200/60 p-5 shadow-sm hover:shadow-md transition-all">
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">{title}</p>
        <div className="p-2 rounded-lg border bg-blue-50 text-[#2E6DA4] border-blue-100"><Icon size={15} /></div>
      </div>
      <p className="text-xl font-bold text-gray-900">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
      {trend && (
        <div className={`flex items-center gap-1 mt-1.5 text-xs font-semibold ${trend > 0 ? "text-emerald-600" : "text-red-500"}`}>
          {trend > 0 ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
          {trend > 0 ? "+" : ""}{trend}% vs mes anterior
        </div>
      )}
    </div>
  );
}

export default function FinanzasProyectado() {
  const [cashFlow, setCashFlow] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const [cfRes, pRes] = await Promise.all([
          axios.get(`${API}/finance/cash-flow`, { withCredentials: true }),
          axios.get(`${API}/projects`, { withCredentials: true }),
        ]);
        setCashFlow(cfRes.data || []);
        setProjects(pRes.data || []);
      } catch {
        toast.error("Error al cargar datos financieros");
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  // Build 12-month projection from historical + budget
  const now = new Date();
  const historicalMonths = cashFlow.slice(-6);

  // Calculate average monthly growth from historical data
  const avgIncome = historicalMonths.length > 0
    ? historicalMonths.reduce((a, m) => a + (m.ingresos || 0), 0) / historicalMonths.length
    : 0;
  const avgExpense = historicalMonths.length > 0
    ? historicalMonths.reduce((a, m) => a + (m.gastos || 0), 0) / historicalMonths.length
    : 0;

  // Total project budgets as reference
  const totalBudget = projects.filter(p => p.status === "activo").reduce((a, p) => a + (p.budget_pyg || 0), 0);

  // Projected data: last 6 months historical + next 6 months projected
  const projectedData = [
    ...historicalMonths.map(m => ({
      month: m.month || "—",
      ingresos_real: m.ingresos || 0,
      gastos_real: m.gastos || 0,
      ingresos_proy: null,
      gastos_proy: null,
      tipo: "real",
    })),
    ...Array.from({ length: 6 }).map((_, i) => {
      const d = new Date(now.getFullYear(), now.getMonth() + i + 1, 1);
      const monthLabel = MONTHS_ES[d.getMonth()] + " " + String(d.getFullYear()).slice(2);
      const growthFactor = 1 + (i * 0.03); // 3% monthly growth estimate
      return {
        month: monthLabel,
        ingresos_real: null,
        gastos_real: null,
        ingresos_proy: Math.round(avgIncome * growthFactor),
        gastos_proy: Math.round(avgExpense * (1 + i * 0.02)),
        tipo: "proyectado",
      };
    }),
  ];

  const lastReal = historicalMonths[historicalMonths.length - 1];
  const nextMonthProy = projectedData.find(d => d.tipo === "proyectado");
  const incomeGrowth = lastReal && lastReal.ingresos > 0 && nextMonthProy ? Math.round(((nextMonthProy.ingresos_proy - lastReal.ingresos) / lastReal.ingresos) * 100) : null;

  // Quarter projections
  const q1 = projectedData.filter(d => d.tipo === "proyectado").slice(0, 3);
  const q2 = projectedData.filter(d => d.tipo === "proyectado").slice(3, 6);
  const q1Income = q1.reduce((a, d) => a + (d.ingresos_proy || 0), 0);
  const q2Income = q2.reduce((a, d) => a + (d.ingresos_proy || 0), 0);

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-bold text-[#2E6DA4] bg-blue-50 px-2 py-0.5 rounded-full uppercase tracking-wide">Finanzas</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Flujo Proyectado</h1>
        <p className="text-sm text-gray-500 mt-0.5">Proyección financiera para los próximos 6 meses basada en datos históricos</p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Ingreso Proy. Próx. Mes" value={formatPYGCompact(nextMonthProy?.ingresos_proy || 0)} trend={incomeGrowth} icon={TrendingUp} />
        <StatCard title="Presupuesto Activo" value={formatPYGCompact(totalBudget)} sub={`${projects.filter(p=>p.status==="activo").length} proyectos activos`} icon={BarChart2} />
        <StatCard title="Proy. Q1 (3 meses)" value={formatPYGCompact(q1Income)} sub="Ingresos estimados" icon={Calendar} />
        <StatCard title="Proy. Q2 (6 meses)" value={formatPYGCompact(q2Income)} sub="Ingresos estimados" icon={DollarSign} />
      </div>

      {/* Projected Chart */}
      <div className="bg-white rounded-2xl border border-gray-200/60 p-5 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-bold text-gray-900">Flujo de Caja — Real vs Proyectado</h3>
            <p className="text-xs text-gray-400 mt-0.5">Últimos 6 meses reales + 6 meses proyectados</p>
          </div>
          <div className="flex items-center gap-3 text-xs text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#2E6DA4] inline-block rounded-full" />Real</span>
            <span className="flex items-center gap-1.5"><span className="w-3 h-0.5 bg-[#2E6DA4] inline-block rounded-full opacity-40 border-dashed" />Proyectado</span>
          </div>
        </div>
        {loading ? (
          <Skeleton className="h-64 w-full rounded-xl" />
        ) : projectedData.length === 0 ? (
          <div className="h-64 flex items-center justify-center text-gray-400 text-sm">Sin datos históricos disponibles</div>
        ) : (
          <div style={{ width: "100%", height: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={projectedData} margin={{ top: 5, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="ingReal" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#2E6DA4" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#2E6DA4" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="ingProy" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#60A5FA" stopOpacity={0.1} />
                  <stop offset="95%" stopColor="#60A5FA" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#F3F4F6" />
              <XAxis dataKey="month" tick={{ fontSize: 11, fill: "#9CA3AF" }} axisLine={false} tickLine={false} />
              <YAxis tickFormatter={(v) => formatPYGCompact(v)} tick={{ fontSize: 10, fill: "#9CA3AF" }} axisLine={false} tickLine={false} width={80} />
              <Tooltip
                contentStyle={{ background: "#1F2937", border: "none", borderRadius: "12px", boxShadow: "0 10px 40px rgba(0,0,0,0.3)" }}
                labelStyle={{ color: "#F9FAFB", fontSize: "12px", fontWeight: "600" }}
                itemStyle={{ color: "#D1D5DB", fontSize: "11px" }}
                formatter={(value, name) => value && value > 0 ? [formatPYGCompact(value), name] : null}
              />
              <Legend wrapperStyle={{ fontSize: "11px", paddingTop: "8px" }} />
              <Area type="monotone" dataKey="ingresos_real" name="Ingresos (Real)" stroke="#2E6DA4" strokeWidth={2.5} fill="url(#ingReal)" connectNulls={false} />
              <Area type="monotone" dataKey="gastos_real" name="Gastos (Real)" stroke="#EF4444" strokeWidth={2} fill="none" connectNulls={false} />
              <Area type="monotone" dataKey="ingresos_proy" name="Ingresos (Proy.)" stroke="#60A5FA" strokeWidth={2} strokeDasharray="6 3" fill="url(#ingProy)" connectNulls={false} />
              <Area type="monotone" dataKey="gastos_proy" name="Gastos (Proy.)" stroke="#FCA5A5" strokeWidth={2} strokeDasharray="6 3" fill="none" connectNulls={false} />
            </AreaChart>
          </ResponsiveContainer>
          </div>
        )}
      </div>

      {/* Monthly projection table */}
      <div className="bg-white rounded-2xl border border-gray-200/60 shadow-sm overflow-hidden">
        <div className="px-5 py-4 border-b border-gray-100">
          <h3 className="text-sm font-bold text-gray-900">Detalle Mensual Proyectado</h3>
          <p className="text-xs text-gray-400 mt-0.5">Estimaciones basadas en historial y tasa de crecimiento proyectada del 3% mensual</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-100 bg-gray-50/50">
                <th className="text-left px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Mes</th>
                <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Ingresos Est.</th>
                <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Gastos Est.</th>
                <th className="text-right px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Resultado Neto</th>
                <th className="text-center px-5 py-3 text-xs font-bold text-gray-500 uppercase tracking-wide">Tipo</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 6 }).map((_, i) => (
                  <tr key={i} className="border-b border-gray-50">
                    {Array.from({ length: 5 }).map((__, j) => <td key={j} className="px-5 py-4"><Skeleton className="h-4 w-full" /></td>)}
                  </tr>
                ))
              ) : projectedData.map((row, i) => {
                const ingresos = row.ingresos_real || row.ingresos_proy || 0;
                const gastos = row.gastos_real || row.gastos_proy || 0;
                const neto = ingresos - gastos;
                return (
                  <tr key={i} className={`border-b border-gray-50 hover:bg-gray-50/50 transition-colors ${row.tipo === "proyectado" ? "bg-blue-50/30" : ""}`}>
                    <td className="px-5 py-3.5 text-sm font-medium text-gray-900">{row.month}</td>
                    <td className="px-5 py-3.5 text-right text-sm font-semibold text-emerald-700">{formatPYGCompact(ingresos)}</td>
                    <td className="px-5 py-3.5 text-right text-sm font-semibold text-red-600">{formatPYGCompact(gastos)}</td>
                    <td className={`px-5 py-3.5 text-right text-sm font-bold ${neto >= 0 ? "text-emerald-700" : "text-red-600"}`}>{neto >= 0 ? "+" : ""}{formatPYGCompact(Math.abs(neto))}</td>
                    <td className="px-5 py-3.5 text-center">
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${row.tipo === "proyectado" ? "bg-blue-100 text-blue-700" : "bg-gray-100 text-gray-600"}`}>
                        {row.tipo === "proyectado" ? "Proyectado" : "Real"}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
